import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ArtistPortalEarningsRow } from 'fym-shared/src/smartlinks/artistPortals';
import { TypedMemo } from '../../../common/types';
import { formatMoney, formatMonth, formatStreamingNumber } from '../streamingFormat';
import { streamingEarningsMessages as messages } from '../streamingEarningsMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from '../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface EarningsMonthBarsProps {
  /** The rows of one currency, `label` = `YYYY-MM`. */
  rows: ArtistPortalEarningsRow[];
  currency: string | null;
}

/**
 * "Net per month" of one currency (design e1). A month with no net amount has no bar (the title
 * says "—"); a net of 0 is a real zero bar. The title adds the units when the source gives them.
 */
const EarningsMonthBarsComponent: React.FC<EarningsMonthBarsProps> = ({ rows, currency }) => {
  const intl = useIntl();
  const months = useMemo(() => [...rows].sort((left, right) => left.label.localeCompare(right.label)), [rows]);
  const top = useMemo(() => Math.max(0, ...months.map((row) => row.netAmount ?? 0)), [months]);

  return (
    <div className={styles.monthBars} role="list">
      {months.map((row) => {
        const net = row.netAmount;
        const units =
          typeof row.quantity === 'number'
            ? ` · ${intl.formatMessage(messages.units)}: ${formatStreamingNumber(intl, row.quantity)}`
            : '';
        return (
          <div
            key={row.label}
            className={styles.monthBar}
            role="listitem"
            data-missing={net === null}
            title={`${formatMonth(intl, row.label)}: ${formatMoney(intl, net, currency)}${units}`}
          >
            {net === null ? null : (
              <span
                className={styles.monthFill}
                style={{ height: `${top > 0 ? Math.round((Math.max(0, net) / top) * 100) : 0}%` }}
              />
            )}
            <span>{formatMonth(intl, row.label)}</span>
          </div>
        );
      })}
    </div>
  );
};

export const EarningsMonthBars = TypedMemo(EarningsMonthBarsComponent);
